import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

// Shared rendering pieces for the Talent & Culture Assessment.
// Used by both the public candidate SurveyPage (/survey/:token) and the
// authed staff MyAssessmentPage (/my/assessment). Keeping them in one
// place means a question-type bug or UX tweak only needs fixing once.

export const CATEGORY_HEADERS = {
  work_style: {
    label: 'Work Style & Personality',
    hint: 'For each set, pick ONE statement MOST like you and ONE LEAST like you.',
  },
  values: {
    label: 'Values & Workplace Tendencies',
    hint: 'Choose the statement closest to your view.',
  },
  behavior: {
    label: 'Real-World Behavioral Reflection',
    hint: 'Choose the option that best reflects your actual tendencies in past workplaces.',
  },
  judgment: {
    label: 'Judgment Scenarios',
    hint: 'For ranking questions, drag (or use the arrows) so 1 is what you would actually do first.',
  },
};

export function isAnswerComplete(question, answer) {
  if (!question.is_required) return true;
  switch (question.question_type) {
    case 'forced_choice':
      // Must be two DIFFERENT options. Compare by visible text — profile
      // tags are admin-only and some seeded questions intentionally share
      // a profile tag across options. Old check (profile must differ)
      // stranded candidates at 96% with no way out.
      return !!(answer?.most && answer?.least
                && (answer.most.text || answer.most.profile)
                && (answer.least.text || answer.least.profile)
                && (answer.most.text || answer.most.profile) !== (answer.least.text || answer.least.profile));
    case 'radio':
      return !!(answer && typeof answer === 'object' && answer.text);
    case 'ranking':
      return Array.isArray(answer) && answer.length === (question.options?.length || 0);
    case 'likert':
      return typeof answer === 'number' && answer >= 1 && answer <= 5;
    case 'checkbox':
      return Array.isArray(answer) && answer.length > 0;
    default:
      return typeof answer === 'string' && answer.trim().length > 0;
  }
}

export function QuestionCard({ question, answer, setAnswer }) {
  return (
    <div id={`q-${question.id}`} className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-5">
      <p className="text-white font-medium mb-4">{question.question_text}</p>
      {question.question_type === 'forced_choice' && (
        <ForcedChoice options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {question.question_type === 'radio' && (
        <RadioGroup options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {question.question_type === 'ranking' && (
        <RankingList options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {(question.question_type === 'text' || question.question_type === 'textarea') && (
        <textarea
          rows={question.question_type === 'textarea' ? 3 : 1}
          value={answer || ''}
          onChange={(e) => setAnswer(e.target.value)}
          className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
          placeholder="Your answer..."
        />
      )}
    </div>
  );
}

function ForcedChoice({ options, value, onChange }) {
  const safeValue = value || {};
  const setPick = (kind, opt) => {
    const next = { ...safeValue, [kind]: { profile: opt.profile, text: opt.text } };
    const other = kind === 'most' ? 'least' : 'most';
    if (next[other]?.profile === opt.profile && next[other]?.text === opt.text) delete next[other];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-xs text-gray-500 px-3">
        <div></div>
        <div className="w-14 text-center">Most</div>
        <div className="w-14 text-center">Least</div>
      </div>
      {options.map((opt, idx) => {
        const isMost = safeValue.most?.text === opt.text;
        const isLeast = safeValue.least?.text === opt.text;
        return (
          <div key={idx} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center bg-gray-700/30 border border-gray-600/40 rounded-lg px-3 py-2">
            <span className="text-gray-200 text-sm">{opt.text}</span>
            <button type="button" onClick={() => setPick('most', opt)}
              className={`w-14 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                isMost ? 'bg-green-500/20 border-green-400 text-green-300'
                       : 'bg-gray-800/40 border-gray-600 text-gray-400 hover:border-gray-500'
              }`}>Most</button>
            <button type="button" onClick={() => setPick('least', opt)}
              className={`w-14 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                isLeast ? 'bg-red-500/20 border-red-400 text-red-300'
                        : 'bg-gray-800/40 border-gray-600 text-gray-400 hover:border-gray-500'
              }`}>Least</button>
          </div>
        );
      })}
    </div>
  );
}

function RadioGroup({ options, value, onChange }) {
  return (
    <div className="space-y-2">
      {options.map((opt, idx) => {
        const optObj = typeof opt === 'string' ? { text: opt, profile: null } : opt;
        const isChecked = value?.text === optObj.text;
        return (
          <label key={idx} className={`flex items-center gap-3 cursor-pointer border rounded-lg px-3 py-2 transition-colors ${
            isChecked ? 'bg-orange-500/10 border-orange-400/50' : 'bg-gray-700/30 border-gray-600/40 hover:border-gray-500'
          }`}>
            <input type="radio" checked={isChecked} onChange={() => onChange(optObj)}
              className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500" />
            <span className="text-gray-200 text-sm">{optObj.text}</span>
          </label>
        );
      })}
    </div>
  );
}

function RankingList({ options, value, onChange }) {
  const list = Array.isArray(value) && value.length === options.length ? value : options;
  const swap = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = list.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {list.map((opt, idx) => (
        <div key={`${opt.text}-${idx}`} className="flex items-center gap-3 bg-gray-700/30 border border-gray-600/40 rounded-lg px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-300 text-sm font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</div>
          <span className="flex-1 text-gray-200 text-sm">{opt.text}</span>
          <div className="flex gap-1">
            <button type="button" onClick={() => swap(idx, -1)} disabled={idx === 0}
              className="p-1.5 rounded-md bg-gray-800/50 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Move up"><ArrowUpIcon className="w-4 h-4" /></button>
            <button type="button" onClick={() => swap(idx, +1)} disabled={idx === list.length - 1}
              className="p-1.5 rounded-md bg-gray-800/50 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Move down"><ArrowDownIcon className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-500 mt-1">1 = most likely, {list.length} = least likely</p>
    </div>
  );
}

// Seed ranking-type answers in random order so candidates can't just hit
// submit without intentional reordering. (Used by both pages to seed
// initial state.)
export function seedRandomRankings(questions) {
  const seed = {};
  for (const q of questions) {
    if (q.question_type === 'ranking' && Array.isArray(q.options)) {
      const shuffled = q.options.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      seed[q.id] = shuffled;
    }
  }
  return seed;
}
